import { useEffect, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Plus, Trash2 } from "lucide-react";

const DEFAULT_TOKEN = "https://images.unsplash.com/photo-1766941234616-ba978cb000e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxyZXRybyUyMGNvbXB1dGVyJTIwdGVybWluYWx8ZW58MHx8fHwxNzc3NTk4MDkyfDA&ixlib=rb-4.1.0&q=85";
const CAREERS = ["Colonial Marine", "Colonial Marshall", "Company Agent", "Kid", "Medic", "Officer", "Pilot", "Roughneck", "Scientist"];

export default function Characters() {
  const { user } = useAuth();
  const [chars, setChars] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [career, setCareer] = useState(CAREERS[2]);

  const load = async () => { try { const r = await api.get("/characters"); setChars(r.data); } catch { toast.error("Load failed"); } };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/characters", { name, career }); toast.success("Character created"); setOpen(false); setName(""); load(); }
    catch { toast.error("Create failed"); }
  };

  const remove = async (e, id, cname) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(`Delete character "${cname}"? This cannot be undone.`)) return;
    try { await api.delete(`/characters/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const isAdmin = user?.role === "admin";

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="heading-mono text-4xl amber amber-glow uppercase tracking-tight">// Crew Registry</h1>
          <div className="mono text-xs text-[#A0A0A0] mt-1 tracking-[0.2em] uppercase">Active Personnel Records</div>
        </div>
        <button data-testid="new-character-btn" className="btn-amber flex items-center gap-2" onClick={() => setOpen(true)}>
          <Plus size={14} strokeWidth={1.5} /> New Character
        </button>
      </div>

      {open && (
        <form onSubmit={create} className="panel p-6 mb-8 space-y-4" data-testid="character-create-form">
          <div className="label-term amber">{">"} New Crew Member</div>
          <div>
            <label className="label-term mb-1 block">Name</label>
            <input data-testid="char-name-input" className="input-term" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <label className="label-term mb-1 block">Career</label>
            <select data-testid="char-career-input" className="input-term" value={career} onChange={(e) => setCareer(e.target.value)}>
              {CAREERS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button data-testid="char-create-submit" className="btn-amber">Create</button>
            <button type="button" className="btn-amber" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="character-grid">
        {chars.length === 0 && <div className="mono text-[#A0A0A0]">// No records found.</div>}
        {chars.map((c) => {
          const canDelete = isAdmin;
          return (
          <Link to={`/characters/${c.id}`} key={c.id} data-testid={`character-card-${c.id}`} className="panel p-4 hover:shadow-[0_0_18px_rgba(255,176,0,0.3)] transition-all relative">
            {canDelete && (
              <button onClick={(e)=>remove(e, c.id, c.name)} className="absolute top-2 right-2 text-[#FF2A2A] p-1 hover:bg-[rgba(255,42,42,0.1)]" data-testid={`delete-character-${c.id}`} title="Delete">
                <Trash2 size={14} />
              </button>
            )}
            <div className="flex items-start gap-4">
              <img src={c.token_path ? fileUrl(c.token_path) : DEFAULT_TOKEN} alt="" className="w-20 h-20 token-img object-cover rounded-full" />
              <div className="flex-1 min-w-0">
                <div className="heading-mono text-lg amber uppercase truncate">{c.name}</div>
                <div className="mono text-xs text-[#A0A0A0] uppercase tracking-widest">{c.career || "Unassigned"}</div>
                <div className="mt-3 grid grid-cols-4 gap-1 mono text-[10px]">
                  <div className="text-center border border-[#333] p-1"><div className="text-[#A0A0A0]">STR</div><div className="amber">{c.attributes?.strength ?? 2}</div></div>
                  <div className="text-center border border-[#333] p-1"><div className="text-[#A0A0A0]">AGI</div><div className="amber">{c.attributes?.agility ?? 2}</div></div>
                  <div className="text-center border border-[#333] p-1"><div className="text-[#A0A0A0]">WIT</div><div className="amber">{c.attributes?.wits ?? 2}</div></div>
                  <div className="text-center border border-[#333] p-1"><div className="text-[#A0A0A0]">EMP</div><div className="amber">{c.attributes?.empathy ?? 2}</div></div>
                </div>
                <div className="mt-2 mono text-[10px] text-[#4AF626]">XP: {c.experience_points} | ♥ {c.health}/{c.max_health} | ${c.cash}</div>
              </div>
            </div>
          </Link>
          );
        })}
      </div>
    </div>
  );
}
